import { css, StyleSheet } from 'aphrodite';
import hardenReactMarkdown from 'harden-react-markdown';
import { memo, useId, useMemo } from 'react';
import ReactMarkdown, { type Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remend from 'remend';
import { components as defaultComponents } from './components';
import { parseMarkdownIntoBlocks } from './parse-blocks';

type HardenReactMarkdownProps = Options & {
	defaultOrigin?: string;
	allowedLinkPrefixes?: string[];
	allowedImagePrefixes?: string[];
};

// Create a hardened version of ReactMarkdown
const HardenedMarkdown: ReturnType<typeof hardenReactMarkdown> = hardenReactMarkdown(ReactMarkdown);

export type StreamdownProps = AphroditeStyled<
	HardenReactMarkdownProps & {
		parseIncompleteMarkdown?: boolean;
		className?: string;
	}
>;

type BlockProps = HardenReactMarkdownProps & {
	content: string;
	shouldParseIncompleteMarkdown: boolean;
};

const Block = memo(
	({ content, shouldParseIncompleteMarkdown, ...props }: BlockProps) => {
		// text-only linkMode: remend's default placeholder href (streamdown:incomplete-link) is an unknown
		// protocol that harden-react-markdown blocks, which would flash ' [blocked]' on links mid-stream
		const parsedContent = useMemo(
			() =>
				typeof content === 'string' && shouldParseIncompleteMarkdown
					? remend(content.trim(), { linkMode: 'text-only' })
					: content,
			[content, shouldParseIncompleteMarkdown]
		);

		return <HardenedMarkdown {...props}>{parsedContent}</HardenedMarkdown>;
	},
	(prevProps, nextProps) => prevProps.content === nextProps.content
);

Block.displayName = 'Block';

export const StreamdownLite = memo(
	({
		children,
		allowedImagePrefixes,
		allowedLinkPrefixes,
		defaultOrigin,
		parseIncompleteMarkdown: shouldParseIncompleteMarkdown = true,
		components,
		rehypePlugins,
		remarkPlugins,
		styles = [],
		...props
	}: StreamdownProps) => {
		const generatedId = useId(); // remove id prop after react 18 and uncomment this line
		// Parse the children to remove incomplete markdown tokens if enabled
		const blocks = useMemo(() => parseMarkdownIntoBlocks(typeof children === 'string' ? children : ''), [children]);

		return (
			<div className={css(s.root, ...styles)} {...props}>
				{blocks.map((block, index) => (
					<Block
						allowedImagePrefixes={allowedImagePrefixes ?? ['*']}
						allowedLinkPrefixes={allowedLinkPrefixes ?? ['*']}
						components={{
							...defaultComponents,
							...components,
						}}
						content={block}
						defaultOrigin={defaultOrigin ?? window.location.origin}
						key={`${generatedId}-block_${index}`}
						rehypePlugins={[...(rehypePlugins ?? [])]}
						remarkPlugins={[remarkGfm, ...(remarkPlugins ?? [])]}
						shouldParseIncompleteMarkdown={shouldParseIncompleteMarkdown}
					/>
				))}
			</div>
		);
	},
	(prevProps, nextProps) => prevProps.children === nextProps.children
);
StreamdownLite.displayName = 'StreamdownLite';

const s = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		':has(*) code': {
			textWrap: 'wrap',
		},
	},
});
